import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    CForm, CFormTextarea, CButton, CRow, CCol, CFormCheck, CContainer, CCard, CCardHeader, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
} from '@coreui/react';

import { apiClient } from 'services/Api';
import { jwtDecode } from 'jwt-decode';
import { triggerAlert } from 'components/globals/AlertNotification';
import logo_stream from 'assets/brand/logo_stream.png';
import ToastNotification from "components/globals/ToastNotification";
import IdiSurveyCompleted from './idiSurveyCompleted';

const evaluationOptions = [
    { label: "Muito insatisfeito", value: 1 },
    { label: "Insatisfeito", value: 2 },
    { label: "Ligeiramente insatisfeito", value: 3 },
    { label: "Ligeiramente satisfeito", value: 4 },
    { label: "Satisfeito", value: 5 },
    { label: "Muito satisfeito", value: 6 }
];

const communicationFields = [
    ['communication_quality', 'Qualidade da comunicação?'],
    ['information_access', 'Facilidade de acesso a informação relevante?'],
    ['information_sharing', 'Transparência na partilha de dados e recursos entre os parceiros?'],
    ['meeting_quality', 'Frequência e qualidade das reuniões conjuntas?']
];

const planningFields = [
    ['partnership_clarity', 'Clareza nos objetivos e metas dos projetos desenvolvidos em parceria?'],
    ['project_clarity', 'Clareza nos objetivos e metas dos projetos desenvolvidos em parceria?'],
    ['deadlines', 'Cumprimento dos prazos estabelecidos?'],
    ['technical_capacity', 'Capacidade técnica e de resposta a novos desafios inerentes aos projetos desenvolvidos em parceria?'],
    ['planning', 'Grau de planeamento e coordenação das tarefas e responsabilidades?'],
];

const resultFields = [
    ['result_quality', 'Qualidade dos resultados obtidos nos projetos desenvolvidos em parceria?'],
    ['result_relevance', 'Relevância e aplicabilidade dos resultados obtidos nos projetos desenvolvidos para os parceiros?'],
    ['project_impact', 'Impacto dos projetos desenvolvidos em parceria para a inovação e desenvolvimento de novas soluções/produtos?'],
    ['fulfillment_level', 'Grau de cumprimento das expectativas definidas no início dos projetos desenvolvidos em parceria?']
];

const partnershipFields = [
    ['confidence_level', 'Nível de confiança entre a STREAM e os parceiros ao longo do projeto?'],
    ['global_satisfaction', 'Satisfação global com a colaboração estabelecida?'],
    ['new_partnership_probability', 'Probabilidade de colaborar novamente com a STREAM em parcerias futuras?']
];

const IdiSurvey = ({ onSubmit }) => {
    const [formData, setFormData] = useState(null);
    const { uuid } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [surveyStatus, setSurveyStatus] = useState(null);
    const [toast, setToast] = useState(null);

    const is_manager = useMemo(() => {
        try {
            const token = localStorage.getItem("access_token");
            return token ? jwtDecode(token).is_manager || false : false;
        } catch {
            return false;
        }
    }, []);

    useEffect(() => {
        const fetchSurvey = async () => {
            try {
                const response = await apiClient.get(`/idi-surveys/${uuid}/`, { skipAuth: true });
                const survey = response.data.survey;
                if (survey.concluded && !is_manager) {
                    setSurveyStatus("completed");
                } else {
                    setFormData(survey);
                }
            } catch (error) {
                const status = error?.response?.status;
                if (!is_manager) {
                    navigate("/questionarios-satisfacao");
                    triggerAlert('Questionário não foi encontrado', 'error');
                } else {
                    setSurveyStatus("not_found");
                }
            }
        };
        fetchSurvey();
    }, [uuid, location.pathname, is_manager, navigate]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        let newValue = type === "radio" ? value : (type === "checkbox" ? checked : value);
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        const allFields = [
            ...communicationFields,
            ...planningFields,
            ...resultFields,
            ...partnershipFields,
        ];

        const incompleteFields = allFields.some(([field]) => formData[field] === null);

        console.log(incompleteFields, formData)
        if (incompleteFields) {
            setToast({ title: 'Inquérito IDI', message: 'Por favor pontue todas as opções.', type: 'error' });
            return;
        }

        try {
            await apiClient.put(`/idi-surveys/${uuid}/`, {
                ...formData,
                concluded: true,
            }, { skipAuth: true });

            triggerAlert('Inquérito submetido com sucesso!', 'success');
            setSurveyStatus("completed");
        } catch (error) {
            triggerAlert('Erro ao submeter o inquérito.', 'error');
            console.error(error);
        }
    };

    const renderEvaluationCard = (title, fields) => (
        <CCard className="mb-3" key={title}>
            <CCardHeader><h5>{title}</h5></CCardHeader>
            <CCardBody>
                <CTable bordered responsive>
                    <CTableHead color="secondary">
                        <CTableRow>
                            <CTableHeaderCell></CTableHeaderCell>
                            {evaluationOptions.map(opt => (
                                <CTableHeaderCell key={opt.label} className="text-center">
                                    {opt.label}
                                </CTableHeaderCell>
                            ))}
                        </CTableRow>
                    </CTableHead>
                    <CTableBody>
                        {fields.map(([field, label]) => (
                            <CTableRow key={field}>
                                <CTableDataCell>{label}</CTableDataCell>
                                {evaluationOptions.map(opt =>
                                    renderEvaluationCell(field, opt.value, formData, setFormData, is_manager)
                                )}
                            </CTableRow>
                        ))}
                    </CTableBody>
                </CTable>
            </CCardBody>
        </CCard>
    );

    const renderEvaluationCell = (field, value, formData, setFormData, is_manager) => {
        const checked = formData[field] === value;
        return (
            <CTableDataCell
                key={value}
                className="text-center"
                style={{ cursor: is_manager ? 'default' : 'pointer' }}
                onClick={() => {
                    if (!is_manager) {
                        setFormData(prev => ({
                            ...prev,
                            [field]: value
                        }));
                    }
                }}
            >
                <label style={{ cursor: is_manager ? 'default' : 'pointer' }}>
                    <CFormCheck
                        type="radio"
                        name={field}
                        value={value}
                        checked={checked}
                        onChange={() => { }}
                        style={{ pointerEvents: 'none' }}
                        disabled={is_manager}
                    />
                </label>
            </CTableDataCell>
        );
    };

    if (surveyStatus === "not_found") {
        return (
            <CContainer className="text-center py-5">
                <img src={logo_stream} alt="Logo" style={{ maxWidth: '300px' }} />
                <h4 className="mt-5">O inquérito não foi encontrado.</h4>
            </CContainer>
        );
    }

    if (surveyStatus === "completed") {
        return <IdiSurveyCompleted />;
    }

    if (!formData) return null;

    return (
        <CContainer>
            <ToastNotification toast={toast} setToast={setToast} />
            <CForm onSubmit={handleSubmit}>
                <CRow className="mb-3">
                    <CCol className="text-center">
                        <img src={logo_stream} alt="Logo" style={{ height: 150, width: 300, objectFit: 'contain' }} />
                    </CCol>
                </CRow>
                <CRow>
                    <CCol className="text-center">
                        <p>No âmbito da certificação do nosso sistema de gestão da Investigação, Desenvolvimento e Inovação (IDI), segundo a NP 4457:2021, e tendo como objetivo melhorar continuamente o nosso desempenho nesta área, solicitamos que responda a este breve inquérito. É nossa forte convicção que a vossa resposta ajudará a melhorar futuras colaborações e projetos.</p>
                    </CCol>
                </CRow>

                {is_manager && (
                    <>
                        <CCard className="mb-3">
                            <CCardHeader>
                                <h5>Parceiro</h5>
                            </CCardHeader>
                            <CCardBody>
                                {formData.partnership_name}
                            </CCardBody>
                        </CCard>
                    </>
                )}

                {renderEvaluationCard("Comunicação e Colaboração", communicationFields)}
                {renderEvaluationCard("Planeamento e Execução", planningFields)}
                {renderEvaluationCard("Resultados e Impacto", resultFields)}
                {renderEvaluationCard("Parceria e Satisfação Geral", partnershipFields)}

                <CCard className="mb-3">
                    <CCardHeader><h5>Houve algum aspeto que correu menos bem?</h5></CCardHeader>
                    <CCardBody>
                        <CFormTextarea name="negative_aspects" rows={5} value={formData.negative_aspects || ''} onChange={handleInputChange} disabled={is_manager} />
                    </CCardBody>
                </CCard>

                <CCard className="mb-3">
                    <CCardHeader><h5>Como a STREAM geriu estas contingências?</h5></CCardHeader>
                    <CCardBody>
                        <CFormTextarea name="contingency_management" rows={5} value={formData.contingency_management || ''} onChange={handleInputChange} disabled={is_manager} />
                    </CCardBody>
                </CCard>

                <CCard className="mb-3">
                    <CCardHeader><h5>Que aspetos da colaboração poderiam ser melhorados?</h5></CCardHeader>
                    <CCardBody>
                        <CFormTextarea name="improvement_suggestions" rows={5} value={formData.improvement_suggestions || ''} onChange={handleInputChange} disabled={is_manager} />
                    </CCardBody>
                </CCard>

                <CCard className="mb-3">
                    <CCardHeader><h5>Quais os principais pontos fortes desta colaboração?</h5></CCardHeader>
                    <CCardBody>
                        <CFormTextarea name="strong_points" rows={5} value={formData.strong_points || ''} onChange={handleInputChange} disabled={is_manager} />
                    </CCardBody>
                </CCard>

                <CCard className="mb-3">
                    <CCardHeader><h5>Outros comentários ou sugestões para melhorar futuros projetos conjuntos.</h5></CCardHeader>
                    <CCardBody>
                        <CFormTextarea name="additional_comments" rows={5} value={formData.additional_comments || ''} onChange={handleInputChange} disabled={is_manager} />
                    </CCardBody>
                </CCard>

                <CRow className="text-center mb-3">
                    <CCol>
                        {is_manager ? (
                            <CButton color="secondary" onClick={() => navigate("/inqueritos-idi")}>Voltar</CButton>
                        ) : (
                            <CButton type="submit" color="success">Concluir inquérito</CButton>
                        )}
                    </CCol>
                </CRow>
            </CForm>
        </CContainer>
    );
};

export default IdiSurvey;
